// Admin full-profile read-only view for an athlete.
// File: frontend/src/pages/AdminAthleteProfilePage.js

import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PowerScoreCard from '../components/PowerScoreCard';
import MentalToughnessCard from '../components/MentalToughnessCard';
import AdminAthleteEditPanel from '../components/AdminAthleteEditPanel';
import Button from '../components/Button';
import { canManageReports, permsFromServer } from '../lib/athleteDetail';
import ShareModal from '../components/share/ShareModal';
import { athleteSubject } from '../components/share/shareSubjects';
import AddToReportModal from '../components/athleteEditor/AddToReportModal';
import MeasurablesCard, { MeasurablesTilesCard } from '../components/athleteEditor/MeasurablesCard';
import VerificationHistoryCard, { VerificationHistoryTable } from '../components/athleteEditor/VerificationHistoryCard';
import { StaffAcademicsCard, StaffCampsCard, AccountActivityCard } from '../components/athleteEditor/StaffProfileSections';
import { ChevronLeftIcon } from '../components/athleteEditor/staffProfileIcons';
import AccoladesCard from '../components/athleteEditor/AccoladesCard';
import EventMarksCard from '../components/athleteEditor/EventMarksCard';
import EquestrianShowRecordCard from '../components/athleteEditor/EquestrianShowRecordCard';
import CredentialsCard from '../components/athleteEditor/CredentialsCard';
import ResultEntriesCard from '../components/athleteEditor/ResultEntriesCard';
import MatchRecordCard from '../components/athleteEditor/MatchRecordCard';
import OffersVisitsCard from '../components/athleteEditor/OffersVisitsCard';
import {
  PhotosRailCard, ContactRailCard, AccountRailCard,
  StaffPhotosCard, StaffContactCard, StaffAccountCard,
} from '../components/athleteEditor/RailCards';
import {
  MENTAL_SKILLS_SUBSCALES as MENTAL_SUBSCALES,
  MENTAL_SKILLS_SUBSCALE_MAX as MENTAL_SUBSCALE_MAX,
  MENTAL_SKILLS_TOTAL_MAX as MENTAL_TOTAL_MAX,
} from '../constants/mentalSkills';
import { useCatalog } from '../context/CatalogContext';
import IdentityBar, { ProfileHeaderCard } from '../components/athleteEditor/IdentityBar';
import { verificationCounts, measurableFieldKeys, formatSavedAgo } from '../lib/athleteEditorModel';
import '../components/Layout.css';
import '../components/athleteEditor/AthleteEditor.css';
import { RailFrame, usesRail } from '../components/AdminShell';

// year_offset is canonical: 0=Freshman … 3=Senior (matches iOS, the source of
// truth). YEAR_LABELS[year_offset] must therefore be in offset order.
const YEAR_LABELS = ['Freshman', 'Sophomore', 'Junior', 'Senior'];

// Server timestamps are UTC with no tz suffix — parse as UTC, render local
// as date + time "M/D/YYYY h:mm am/pm". (fmtTimestamp/humanize, formerly
// used by the now-moved-to-components Photos/Cards/Videos sections, were
// removed as dead code — see VerificationHistoryCard.jsx / RailCards.jsx
// for their replacements.)
function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + 'Z');
  if (isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase();
  return `${date} ${time}`;
}

function VerifiedBadge({ verified }) {
  if (!verified) return null;
  return (
    <span
      title="Verified"
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 14, height: 14, borderRadius: '50%', background: '#4D8064',
        color: 'white', fontSize: 9, fontWeight: 700, marginLeft: 6, lineHeight: 1,
      }}
    >✓</span>
  );
}

function GameStatsSection({ stats }) {
  if (!stats || stats.length === 0) return null;
  // Group by position slot, then year. Use season_year when present, else
  // year_offset mapped to a class label.
  const byPos = {};
  stats.forEach(s => {
    const posKey = `${s.position_slot}_${s.position || ''}`;
    const yearKey = s.season_year != null ? s.season_year : s.year_offset;
    if (!byPos[posKey]) byPos[posKey] = { position: s.position || `Position ${s.position_slot}`, years: {} };
    if (!byPos[posKey].years[yearKey]) {
      byPos[posKey].years[yearKey] = {
        label: s.season_year != null ? String(s.season_year) : (YEAR_LABELS[s.year_offset] || `Year ${s.year_offset}`),
        sort: s.season_year != null ? s.season_year : (s.year_offset ?? 0),
        rows: [],
      };
    }
    byPos[posKey].years[yearKey].rows.push({ name: s.stat_name, value: s.stat_value });
  });
  return (
    <div className="form-section">
      <div className="form-section__title">Game Stats</div>
      {Object.values(byPos).map((group, gi) => (
        <div key={gi} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#3F6B53', marginBottom: 8 }}>{group.position}</div>
          {Object.values(group.years)
            .sort((a, b) => b.sort - a.sort)
            .map((yr, yi) => (
              <div key={yi} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>{yr.label}</div>
                <div className="admin-stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                  {yr.rows.map((r, ri) => (
                    <div className="admin-stat" key={ri}>
                      <div className="admin-stat__label">{r.name}</div>
                      <div className="admin-stat__value">{r.value != null && r.value !== '' ? r.value : '—'}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      ))}
    </div>
  );
}

const ACADEMIC_ROWS = [
  ['gpa_weighted', 'gpa_weighted_verified', 'GPA (Weighted)', v => Number(v).toFixed(2)],
  ['gpa_unweighted', 'gpa_unweighted_verified', 'GPA (Unweighted)', v => Number(v).toFixed(2)],
  ['act_score', 'act_score_verified', 'ACT', v => `${v}`],
  ['sat_score', 'sat_score_verified', 'SAT', v => `${v}`],
];

// Always renders all four rows, showing '—' for the ones with no value. It
// used to drop the empty ones and return null when all four were empty, which
// meant a profile with no academics at all showed no academics block — the
// staff reading this page could not tell "missing" from "not applicable".
function AcademicsSection({ profile }) {
  const hasValue = (f) => profile[f] != null && profile[f] !== '';
  return (
    <div className="form-section">
      <div className="form-section__title">Academics</div>
      <div className="admin-stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        {ACADEMIC_ROWS.map(([field, verifiedField, label, fmt]) => (
          <div className="admin-stat" key={field}>
            <div className="admin-stat__label">{label}</div>
            <div className="admin-stat__value">
              {hasValue(field) ? fmt(profile[field]) : '—'}
              <VerifiedBadge verified={hasValue(field) && profile[verifiedField]} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Camps the athlete has checked in to, most-recent first. `profile.camps` is
// supplied by GET /athletes/{id}/profile (attended_at set, registrant_type athlete).
function CampsSection({ profile }) {
  const camps = profile.camps || [];
  const fmtDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };
  return (
    <div className="form-section">
      <div className="form-section__title">Camps Attended</div>
      {camps.length === 0 ? (
        <div style={{ fontSize: 13, color: '#94a3b8' }}>None on record</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {camps.map((c) => (
            <div key={c.id} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{c.name}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                {[fmtDate(c.date), c.location].filter(Boolean).join(' · ') || '—'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MentalSkillsSection({ profile, emptyText }) {
  const total = profile.mental_skills_total;
  const subs = profile.mental_skills_subscales;
  if (total == null) {
    if (!emptyText) return null;
    return (
      <div className="form-section">
        <div className="form-section__title">Mental Skills (ASCI-28)</div>
        <div style={{ fontSize: 13, color: '#94a3b8' }}>{emptyText}</div>
      </div>
    );
  }
  return (
    <div className="form-section">
      <div className="form-section__title">Mental Skills (ASCI-28)</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 12 }}>
        <span style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>{total}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8' }}>/ {MENTAL_TOTAL_MAX}</span>
      </div>
      {subs && MENTAL_SUBSCALES.map(sub => {
        const v = subs[sub.key] ?? 0;
        const pct = Math.max(0, Math.min(1, v / MENTAL_SUBSCALE_MAX));
        return (
          <div key={sub.key} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span style={{ color: '#0f172a', fontWeight: 500 }}>{sub.label}</span>
              <span style={{ color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{v} / {MENTAL_SUBSCALE_MAX}</span>
            </div>
            <div style={{ height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${pct * 100}%`, height: '100%', background: '#4D8064', borderRadius: 4 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AdminAthleteProfilePageContent() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { API_URL, user } = useAuth();

  // Adding to a report is a separate capability from the verify/edit perms
  // below, and a NARROWER one — coach can open this page but every
  // /api/reports write for them is admin-only, so they must not see the control.
  const canAddToReport = canManageReports(user?.role);
  const [showAddToReport, setShowAddToReport] = useState(false);
  // Sharing one athlete with coaches: staff and partners only (coaches can open this page).
  const canShare = ['admin', 'internal_staff', 'partner'].includes(user?.role);
  const [showShare, setShowShare] = useState(false);

  // Auth-header helper for ad-hoc axios calls made outside the initial
  // Promise.all load below (e.g. MeasurablesEditor's per-field saves) —
  // mirrors the inline `cfg` built in the load effect.
  const getConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const [profile, setProfile] = useState(null);
  // Server-authoritative permissions (admin/internal_staff/coach/partner all reach
  // this page now that /athletes/:userId is open to them). A partner's rights
  // depend on whether THIS athlete is theirs — the role name alone can't tell
  // that — so perms follow the profile's `perms` block once it loads, falling
  // back to the role-name guess (permsFromServer's own fallback) until then.
  const perms = permsFromServer(profile?.perms, user?.role);
  const [logs, setLogs] = useState([]);
  const [gameStats, setGameStats] = useState([]);
  const [videos, setVideos] = useState([]);
  const [cards, setCards] = useState([]);
  const [coachNotes, setCoachNotes] = useState([]);
  const [accolades, setAccolades] = useState([]);
  const [marks, setMarks] = useState([]);
  const [results, setResults] = useState([]);
  const [resultsSummary, setResultsSummary] = useState({ ncea: {}, ihsa: {} });
  const [credentials, setCredentials] = useState([]);
  // Wave-2 ledgers (staff read + verify).
  const [resultEntries, setResultEntries] = useState([]);
  const [tennisMatches, setTennisMatches] = useState([]);
  const [tennisRecords, setTennisRecords] = useState({ singles: {}, doubles: {} });
  const [wrestlingMatches, setWrestlingMatches] = useState([]);
  const [wrestlingRecords, setWrestlingRecords] = useState({ folkstyle: {}, freestyle: {}, greco: {} });
  const [wrestlingPlacements, setWrestlingPlacements] = useState([]);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(true);
  const [error, setError] = useState('');
  // Seeded from the URL only. Whether THIS user may edit THIS athlete is the
  // server's perms block, which isn't loaded yet; every use below is already
  // gated on perms.canEditProfile. (The role-name fallback used to gate the
  // seed and was always false for a partner, so ?edit=1 never opened for them.)
  const [editMode, setEditMode] = useState(
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('edit') === '1'
  );
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [saveError, setSaveError] = useState('');

  // Re-render every second while in edit mode so the IdentityBar's "✓ SAVED Ns
  // AGO" text keeps advancing without needing a new save to trigger a render.
  // Not needed (and not shown) in view mode, so the interval is gated on
  // editMode to avoid re-rendering the whole page every second for no reason.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!editMode) return;
    const t = setInterval(() => forceTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, [editMode]);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    const cfg = { headers: { Authorization: `Bearer ${token}` } };

    setLoading(true);
    setLogsLoading(true);
    setError('');

    Promise.all([
      axios.get(`${API_URL}/athletes/${userId}/profile`, cfg),
      axios.get(`${API_URL}/athletes/${userId}/verification-history`, cfg).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/game-stats/player/${userId}`, cfg).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/videos/player/${userId}`, cfg).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/player-cards/by-user/${userId}`, cfg).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/admin/users/${userId}/membership`, cfg).catch(() => ({ data: null })),
      axios.get(`${API_URL}/saved-athletes/athlete/${userId}/all-notes`, cfg).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/athletes/${userId}/accolades`, cfg).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/event-marks/athlete/${userId}`, cfg).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/equestrian-results/athlete/${userId}`, cfg).catch(() => ({ data: { results: [], summary: { ncea: {}, ihsa: {} } } })),
      axios.get(`${API_URL}/credentials/athlete/${userId}`, cfg).catch(() => ({ data: [] })),
    ]).then(([profileRes, logsRes, statsRes, videosRes, cardsRes, membershipRes, coachNotesRes, accoladesRes, marksRes, resultsRes, credentialsRes]) => {
      if (cancelled) return;
      setProfile(profileRes.data);
      setLogs(Array.isArray(logsRes.data) ? logsRes.data : []);
      setGameStats(Array.isArray(statsRes.data) ? statsRes.data : []);
      setVideos(Array.isArray(videosRes.data) ? videosRes.data : []);
      setMembership(membershipRes.data || null);
      setCoachNotes(Array.isArray(coachNotesRes.data) ? coachNotesRes.data : []);
      setAccolades(Array.isArray(accoladesRes.data) ? accoladesRes.data : []);
      setMarks(Array.isArray(marksRes.data) ? marksRes.data : []);
      setResults(Array.isArray(resultsRes.data?.results) ? resultsRes.data.results : []);
      setResultsSummary(resultsRes.data?.summary || { ncea: {}, ihsa: {} });
      setCredentials(Array.isArray(credentialsRes.data) ? credentialsRes.data : []);
      const cd = cardsRes.data;
      setCards(Array.isArray(cd) ? cd : (Array.isArray(cd?.cards) ? cd.cards : []));
    }).catch(err => {
      if (cancelled) return;
      setError(err.response?.data?.detail || err.message || 'Failed to load profile.');
    }).finally(() => {
      if (cancelled) return;
      setLoading(false);
      setLogsLoading(false);
    });

    return () => { cancelled = true; };
  }, [userId, API_URL]);

  const catalog = useCatalog();
  // Sport -> catalog slug -> grouped measurables, resolved the same way
  // MeasurablesCard does. Shared by the verified/unverified/empty counts
  // (identity bar) and VerificationHistoryCard's humanized stat labels.
  const sportSlug = useMemo(() => {
    const sportName = profile?.sport || 'Football';
    return (catalog.sports.find(s => s.name === sportName) || {}).slug || 'football';
  }, [profile, catalog]);
  const groupsForSport = useMemo(
    () => (profile ? catalog.groupedMeasurablesFor(sportSlug) : []),
    [profile, catalog, sportSlug]);
  const trackPositions = useMemo(
    () => (sportSlug === 'track' ? catalog.positionsFor('track') : []),
    [catalog, sportSlug]);
  const statsMode = useMemo(
    () => (profile ? catalog.statsModeFor(sportSlug) : 'game_stats'),
    [profile, catalog, sportSlug]);
  const resultPositions = useMemo(
    () => (statsMode === 'result_entries' ? catalog.positionsFor(sportSlug) : []),
    [catalog, sportSlug, statsMode]);

  // Load the Wave-2 ledger the athlete's sport routes to, once the sport slug is
  // known (mirrors the main fetch but gated on stats_mode; result_entries needs
  // the sport query param, match_record branches tennis vs wrestling).
  useEffect(() => {
    if (!profile) return;
    let cancelled = false;
    const cfg = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    // Reset every ledger before (re)fetching so a fetch error or fast athlete
    // navigation can never render a prior athlete's rows on this staff/verify
    // surface (the page is reused across athletes without remounting).
    setResultEntries([]);
    setTennisMatches([]); setTennisRecords({ singles: {}, doubles: {} });
    setWrestlingMatches([]); setWrestlingRecords({ folkstyle: {}, freestyle: {}, greco: {} });
    setWrestlingPlacements([]);
    if (statsMode === 'result_entries') {
      axios.get(`${API_URL}/result-entries/athlete/${userId}?sport=${encodeURIComponent(sportSlug)}`, cfg)
        .then(res => { if (!cancelled) setResultEntries(Array.isArray(res.data) ? res.data : []); })
        .catch(() => {});
    } else if (statsMode === 'match_record' && sportSlug === 'tennis') {
      axios.get(`${API_URL}/tennis-matches/athlete/${userId}`, cfg)
        .then(res => { if (cancelled) return; setTennisMatches(res.data?.matches || []); setTennisRecords(res.data?.records || { singles: {}, doubles: {} }); })
        .catch(() => {});
    } else if (statsMode === 'match_record' && sportSlug === 'wrestling') {
      axios.get(`${API_URL}/wrestling/matches/athlete/${userId}`, cfg)
        .then(res => { if (cancelled) return; setWrestlingMatches(res.data?.matches || []); setWrestlingRecords(res.data?.records || { folkstyle: {}, freestyle: {}, greco: {} }); })
        .catch(() => {});
      axios.get(`${API_URL}/wrestling/placements/athlete/${userId}`, cfg)
        .then(res => { if (!cancelled) setWrestlingPlacements(res.data?.placements || []); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [profile, statsMode, sportSlug, userId, API_URL]);
  // SMTQ response-quality flags are internal: only admin / internal_staff may
  // fetch or see them. This page is also the coach and partner profile, so this
  // role check is what keeps quality off their screens (the server also sends
  // them only `{summary}`).
  const canSeeSmtqQuality = user?.role === 'admin' || user?.role === 'internal_staff';
  const hasSmtq = !!profile?.smtq_summary;
  const [smtqQuality, setSmtqQuality] = useState(null);
  useEffect(() => {
    setSmtqQuality(null);
    if (!canSeeSmtqQuality || !hasSmtq) return undefined;
    let cancelled = false;
    const cfg = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    axios.get(`${API_URL}/smtq/users/${userId}/latest`, cfg)
      .then(res => { if (!cancelled) setSmtqQuality(res.data?.quality || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canSeeSmtqQuality, hasSmtq, userId, API_URL]);
  const counts = useMemo(() => {
    if (!profile) return { verified: 0, unverified: 0, empty: 0 };
    return verificationCounts(profile, measurableFieldKeys(groupsForSport, profile));
  }, [profile, groupsForSport]);
  const savedAgo = formatSavedAgo(lastSavedAt, Date.now()); // re-derives every render; ticker above keeps it fresh

  if (loading) {
    return (
      <div className="main-content">
        <div className="card" style={{ margin: '24px auto', maxWidth: 1100, padding: 24 }}>Loading profile…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-content">
        <div className="card" style={{ margin: '24px auto', maxWidth: 1100, padding: 24 }}>
          <div style={{ color: '#b91c1c', fontWeight: 600, marginBottom: 8 }}>Error</div>
          <div style={{ color: '#475569' }}>{error}</div>
          <Button variant="secondary" style={{ marginTop: 16 }} onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/admin/users'))}>
            ← Back
          </Button>
        </div>
      </div>
    );
  }

  const u = profile?.user || {};
  const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim() || '(unnamed)';

  // Send Message — lifted from the retired player-detail modal.
  const handleSendMessage = () => {
    const targetUser = {
      id: Number(userId),
      name: fullName,
      role: u.role || 'player_parent',
    };
    navigate('/messages', { state: { targetUser } });
  };

  // Enroll (partner-only, server-gated by perms.canEnroll) — a non-owned
  // athlete 404s rather than 403ing (global constraint), and an already-owned
  // one 409s; both land in the same inline error banner as everything else on
  // this page rather than a toast.
  const handleEnroll = async () => {
    try {
      await axios.post(`${API_URL}/concierge/${userId}/enroll`, {}, getConfig());
      navigate(`/athletes/${userId}/concierge`);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(d?.message || d || 'Could not enroll this athlete.');
    }
  };

  const onBack = () => (window.history.length > 1 ? navigate(-1) : navigate('/admin/users'));
  const addToReportModal = showAddToReport && (
    <AddToReportModal
      athleteUserId={userId}
      athleteName={`${u.first_name || ''} ${u.last_name || ''}`.trim()}
      athleteSportSlug={profile?.sport ? sportSlug : ''}
      athleteSportName={profile?.sport || ''}
      apiUrl={API_URL}
      getConfig={getConfig}
      onClose={() => setShowAddToReport(false)}
    />
  );

  const shareModal = showShare && profile?.profile_url && (
    <ShareModal
      subject={athleteSubject({
        userId,
        name: `${u.first_name || ''} ${u.last_name || ''}`.trim(),
        position: profile.position_primary,
        graduationYear: profile.graduation_year,
        highSchool: profile.high_school,
        state: profile.state || profile.high_school_state,
        publicUrl: `${window.location.origin}/athlete/${u.uid || profile.uid}/${profile.profile_url}`,
      })}
      onClose={() => setShowShare(false)}
    />
  );

  // Staff and partners (the roles RailFrame frames) get the `.adm` read view.
  // Coaches reach this same route without the rail and keep the view below
  // unchanged, and so does Edit mode for everyone.
  if (usesRail(user?.role) && !(editMode && perms.canEditProfile)) {
    const sportCards = (
      <>
        <AccoladesCard
          userId={userId} accolades={accolades} setAccolades={setAccolades}
          canVerify={perms.canVerify} apiUrl={API_URL} getConfig={getConfig}
        />
        {sportSlug === 'track' && (
          <EventMarksCard
            userId={userId} marks={marks} setMarks={setMarks}
            canVerify={perms.canVerify} apiUrl={API_URL} getConfig={getConfig}
            positions={trackPositions}
          />
        )}
        {sportSlug === 'equestrian' && (
          <>
            <EquestrianShowRecordCard
              userId={userId} results={results} setResults={setResults}
              summary={resultsSummary} canVerify={perms.canVerify}
              apiUrl={API_URL} getConfig={getConfig}
            />
            <CredentialsCard
              userId={userId} credentials={credentials} setCredentials={setCredentials}
              canVerify={perms.canVerify} apiUrl={API_URL} getConfig={getConfig}
            />
          </>
        )}
        {statsMode === 'result_entries' && (
          <ResultEntriesCard
            userId={userId} entries={resultEntries} setEntries={setResultEntries}
            canVerify={perms.canVerify} apiUrl={API_URL} getConfig={getConfig}
            positions={resultPositions} sportSlug={sportSlug}
          />
        )}
        {statsMode === 'match_record' && (
          <MatchRecordCard
            userId={userId} sportSlug={sportSlug}
            canVerify={perms.canVerify} apiUrl={API_URL} getConfig={getConfig}
            tennisMatches={tennisMatches} setTennisMatches={setTennisMatches} tennisRecords={tennisRecords}
            wrestlingMatches={wrestlingMatches} setWrestlingMatches={setWrestlingMatches}
            wrestlingRecords={wrestlingRecords}
            wrestlingPlacements={wrestlingPlacements} setWrestlingPlacements={setWrestlingPlacements}
          />
        )}
        {profile.power_score && (
          <section className="adm__card"><PowerScoreCard score={profile.power_score} /></section>
        )}
        {gameStats.length > 0 && (
          <section className="adm__card"><GameStatsSection stats={gameStats} /></section>
        )}
      </>
    );

    return (
      <div className="main-content adm-profile">
        <button type="button" className="adm-back" onClick={onBack}>
          <ChevronLeftIcon />{user?.role === 'partner' ? 'My athletes' : 'Users'}
        </button>
        <ProfileHeaderCard
          profile={profile} user={u} perms={perms} counts={counts}
          onEdit={() => setEditMode(true)}
          onSendMessage={handleSendMessage}
          onConcierge={perms.canOpenConcierge ? () => navigate(`/athletes/${userId}/concierge`) : null}
          onEnroll={perms.canEnroll ? handleEnroll : null}
          onAddToReport={canAddToReport ? () => setShowAddToReport(true) : null}
          onShare={canShare ? () => setShowShare(true) : null}
          publicProfileUrl={profile.profile_url ? `/athlete/${profile.profile_url}` : null}
          membership={perms.canSeeFinancial ? membership : null}
        />
        <div className="adm-profile__grid">
          <div className="adm-profile__main">
            <MeasurablesTilesCard
              userId={userId} profile={profile} perms={perms} apiUrl={API_URL}
              getConfig={getConfig} logs={logs}
              onChange={(patch) => setProfile(prev => ({ ...prev, ...patch }))}
              onSaved={() => { setSaveError(''); setLastSavedAt(Date.now()); }}
            />
            {sportCards}
            <StaffAcademicsCard profile={profile} />
            {/* Both mental assessments always show for staff, SMTQ under ASCI-28,
                so "not taken yet" is visible rather than a missing card. */}
            <section className="adm__card">
              <MentalSkillsSection profile={profile} emptyText="Not taken yet" />
            </section>
            <section className="adm__card">
              <MentalToughnessCard
                summary={profile.smtq_summary}
                quality={canSeeSmtqQuality ? smtqQuality : null}
                emptyText="Not taken yet"
              />
            </section>
            <VerificationHistoryTable logs={logs} loading={logsLoading} groups={groupsForSport} />
            <OffersVisitsCard
              userId={userId}
              profile={profile}
              canEdit={perms.canEditProfile}
              apiUrl={API_URL}
              getConfig={getConfig}
              onChange={(patch) => setProfile(prev => ({ ...prev, ...patch }))}
            />
            {coachNotes.length > 0 && (
              <section className="adm__card">
                <div className="adm-card__head"><h2 className="adm-card__title">Coach notes</h2></div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {coachNotes.map((n, i) => (
                    <div key={`${n.coach_user_id}-${i}`} style={{ borderLeft: '3px solid #EEF2F0', paddingLeft: 12 }}>
                      <div style={{ color: '#07131F', fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{n.notes}</div>
                      <div style={{ marginTop: 4, color: '#8CA096', fontSize: 12 }}>
                        — {n.coach_name}{n.updated_at ? ` · ${fmtDateTime(n.updated_at)}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
          <aside className="adm-profile__rail">
            <StaffPhotosCard
              userId={userId} profile={profile} apiUrl={API_URL} cards={cards} videos={videos}
              personalVideoUrl={profile.personal_video_url}
              fixSchoolLogoHref={(user?.role === 'admin' || user?.role === 'internal_staff')
                ? `/admin/school-logos?athlete=${userId}` : null}
            />
            <StaffContactCard
              profile={profile} user={u} sportSlug={sportSlug} athleteId={userId} apiUrl={API_URL} getConfig={getConfig}
              canManageGuardians={user?.role === 'admin' || user?.role === 'internal_staff'}
            />
            <StaffAccountCard
              profile={profile} user={u}
              membership={perms.canSeeFinancial ? membership : null}
              activity={profile.activity}
            />
            <StaffCampsCard profile={profile} />
            {profile.activity && <AccountActivityCard user={u} activity={profile.activity} />}
          </aside>
        </div>
        {addToReportModal}
        {shareModal}
      </div>
    );
  }

  return (
    <div className="main-content">
      <IdentityBar
        profile={profile} user={u} perms={perms} counts={counts}
        editMode={editMode && perms.canEditProfile}
        onToggleEdit={() => setEditMode(v => !v)}
        savedAgo={savedAgo} saveError={saveError}
        onBack={onBack}
        onSendMessage={handleSendMessage}
        onConcierge={perms.canOpenConcierge ? () => navigate(`/athletes/${userId}/concierge`) : null}
        onEnroll={perms.canEnroll ? handleEnroll : null}
        onAddToReport={canAddToReport ? () => setShowAddToReport(true) : null}
        onShare={canShare ? () => setShowShare(true) : null}
        publicProfileUrl={profile.profile_url ? `/athlete/${profile.profile_url}` : null}
        membership={perms.canSeeFinancial ? membership : null}
      />
      <div style={{ maxWidth: 1100, margin: '24px auto', padding: '0 16px' }}>
        {editMode && perms.canEditProfile ? (
          <AdminAthleteEditPanel
            userId={userId}
            profile={profile}
            videos={videos}
            cards={cards}
            membership={perms.canSeeFinancial ? membership : null}
            perms={perms}
            logs={logs}
            getConfig={getConfig}
            onMembershipChange={setMembership}
            onClose={() => setEditMode(false)}
            onSaved={(patch) => setProfile(prev => ({ ...prev, ...patch }))}
            /* One saved coach row at a time (the server's authoritative
               response, carrying sport_slug). Merged here against `prev` —
               replace the row for that slug, append when it's new — so two
               saves racing for different sports both survive and neither the
               panel nor this page has to hold a shared list to clobber. */
            onSportCoachSaved={(row) => setProfile(prev => ({
              ...prev,
              sport_coaches: [
                ...(prev.sport_coaches || []).filter(r => r.sport_slug !== row.sport_slug),
                row,
              ],
            }))}
            onSaveStatus={({ savedAt, error }) => {
              if (error) setSaveError(error);
              else { setSaveError(''); setLastSavedAt(savedAt); }
            }}
          />
        ) : (
        <div className="ae-page">
          <div className="ae-main">
            {/* Measurables — flat card (1a) with inline verify seals, gated by
                perms. The bulk verify/unverify ledger now lives under Edit ▸
                Measurables (AdminAthleteEditPanel). */}
            <MeasurablesCard
              userId={userId} profile={profile} perms={perms} apiUrl={API_URL}
              getConfig={getConfig} logs={logs}
              onChange={(patch) => setProfile(prev => ({ ...prev, ...patch }))}
              onSaved={() => { setSaveError(''); setLastSavedAt(Date.now()); }}
            />

            <VerificationHistoryCard logs={logs} loading={logsLoading} groups={groupsForSport} />

            <AccoladesCard
              userId={userId} accolades={accolades} setAccolades={setAccolades}
              canVerify={perms.canVerify} apiUrl={API_URL} getConfig={getConfig}
            />

            {sportSlug === 'track' && (
              <EventMarksCard
                userId={userId} marks={marks} setMarks={setMarks}
                canVerify={perms.canVerify} apiUrl={API_URL} getConfig={getConfig}
                positions={trackPositions}
              />
            )}

            {sportSlug === 'equestrian' && (
              <>
                <EquestrianShowRecordCard
                  userId={userId} results={results} setResults={setResults}
                  summary={resultsSummary} canVerify={perms.canVerify}
                  apiUrl={API_URL} getConfig={getConfig}
                />
                <CredentialsCard
                  userId={userId} credentials={credentials} setCredentials={setCredentials}
                  canVerify={perms.canVerify} apiUrl={API_URL} getConfig={getConfig}
                />
              </>
            )}

            {statsMode === 'result_entries' && (
              <ResultEntriesCard
                userId={userId} entries={resultEntries} setEntries={setResultEntries}
                canVerify={perms.canVerify} apiUrl={API_URL} getConfig={getConfig}
                positions={resultPositions} sportSlug={sportSlug}
              />
            )}

            {statsMode === 'match_record' && (
              <MatchRecordCard
                userId={userId} sportSlug={sportSlug}
                canVerify={perms.canVerify} apiUrl={API_URL} getConfig={getConfig}
                tennisMatches={tennisMatches} setTennisMatches={setTennisMatches} tennisRecords={tennisRecords}
                wrestlingMatches={wrestlingMatches} setWrestlingMatches={setWrestlingMatches}
                wrestlingRecords={wrestlingRecords}
                wrestlingPlacements={wrestlingPlacements} setWrestlingPlacements={setWrestlingPlacements}
              />
            )}

            {/* Kept cards — reskinned to the flat .ae-card shell, same conditions as before */}
            {profile.power_score && (
              <div className="ae-card ae-card--pad">
                <PowerScoreCard score={profile.power_score} />
              </div>
            )}

            {gameStats.length > 0 && (
              <div className="ae-card ae-card--pad">
                <GameStatsSection stats={gameStats} />
              </div>
            )}

            <div className="ae-card ae-card--pad">
              <AcademicsSection profile={profile} />
            </div>

            {profile.mental_skills_total != null && (
              <div className="ae-card ae-card--pad">
                <MentalSkillsSection profile={profile} />
              </div>
            )}

            {profile.smtq_summary && (
              <div className="ae-card ae-card--pad">
                <MentalToughnessCard summary={profile.smtq_summary} quality={canSeeSmtqQuality ? smtqQuality : null} />
              </div>
            )}

            {/* Editable inline for admin/internal_staff (like MeasurablesCard),
                not gated on editMode — this card lives in the read-view column,
                which only renders when editMode is off. */}
            <OffersVisitsCard
              userId={userId}
              profile={profile}
              canEdit={perms.canEditProfile}
              apiUrl={API_URL}
              getConfig={getConfig}
              onChange={(patch) => setProfile(prev => ({ ...prev, ...patch }))}
            />

            <div className="ae-card ae-card--pad">
              <CampsSection profile={profile} />
            </div>

            {coachNotes.length > 0 && (
              <div className="ae-card ae-card--pad">
                <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Coach Notes</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {coachNotes.map((n, i) => (
                    <div key={`${n.coach_user_id}-${i}`} style={{ borderLeft: '3px solid #e2e8f0', paddingLeft: 12 }}>
                      <div style={{ color: '#0f172a', fontSize: 14, whiteSpace: 'pre-wrap' }}>{n.notes}</div>
                      <div style={{ marginTop: 4, color: '#94a3b8', fontSize: 12 }}>
                        — {n.coach_name}{n.updated_at ? ` · ${fmtDateTime(n.updated_at)}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="ae-rail">
            <PhotosRailCard
              userId={userId} profile={profile} apiUrl={API_URL} cards={cards} videos={videos}
              personalVideoUrl={profile.personal_video_url}
              fixSchoolLogoHref={(user?.role === 'admin' || user?.role === 'internal_staff')
                ? `/admin/school-logos?athlete=${userId}` : null}
            />
            <ContactRailCard profile={profile} user={u} sportSlug={sportSlug} />
            <AccountRailCard profile={profile} user={u} />
          </aside>
        </div>
        )}
      </div>

      {addToReportModal}
      {shareModal}
    </div>
  );
}

// Admin, staff and partner get the left rail around this page; every other role (coaches reach
// this route too) gets the page exactly as before. See RailFrame in components/AdminShell.js.
export default function AdminAthleteProfilePage() {
  return (
    <RailFrame active="users">
      <AdminAthleteProfilePageContent />
    </RailFrame>
  );
}
